//#region Imports

import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Crud } from '../../shared/services/crud/crud.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { AuthService } from '../../shared/services/auth/auth.service';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { AnimatedBannerComponent } from '../../shared/components/animated-banner/animated-banner.component';
import { PasswordFeedbackComponent } from '../../shared/components/password-feedback/password-feedback.component';
import { ButtonTypeEnum } from '../../shared/components/button/types/button-type.enum';
import { ButtonRadiusEnum } from '../../shared/components/button/types/button-radius.enum';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { SpinnerSizeEnum } from '../../shared/components/spinner/types/spinner-size.enum';
import { SpinnerColorEnum } from '../../shared/components/spinner/types/spinner-color.enum';

//#endregion

interface UserForm {
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

const resetFieldLabels = {
  primary: 'senha',
  confirmation: 'confirmar senha',
} as const;

@Component({
  selector: 'l-reset-pwd',
  templateUrl: './reset-pwd.component.html',
  styleUrl: './reset-pwd.component.scss',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    IconComponent,
    AnimatedBannerComponent,
    PasswordFeedbackComponent,
    SpinnerComponent,
  ],
})
export class ResetPwdComponent extends Crud<UserForm> {

  public isLoading = false;

  public resetSucceeded = false;

  public hidePassword = true;

  public hideConfirmPassword = true;

  public readonly buttonTypes = ButtonTypeEnum;

  public readonly buttonRadius = ButtonRadiusEnum;

  public readonly spinnerSize = SpinnerSizeEnum;

  public readonly spinnerColors = SpinnerColorEnum;

  public override form = new FormGroup<UserForm>({
    password: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    confirmPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  public override mapField = {
    password: resetFieldLabels.primary,
    confirmPassword: resetFieldLabels.confirmation,
  };

  private readonly authService = inject(AuthService);

  private readonly toastService = inject(ToastService);

  private readonly route = inject(ActivatedRoute);

  private readonly router = inject(Router);

  public async submit(): Promise<void> {
    const payload = this.form.getRawValue();
    const resetPasswordCode = this.getResetPasswordCode();

    if (!resetPasswordCode) {
      this.toastService.error('Código de redefinição inválido ou ausente.');
      return;
    }

    const passwordError = this.validatePassword(payload.password, payload.confirmPassword);

    if (passwordError) {
      this.toastService.error(passwordError);
      return;
    }

    try {
      this.isLoading = true;
      await this.authService.resetPassword(payload.password, payload.confirmPassword, resetPasswordCode);
      this.resetSucceeded = true;
    } catch (error: any) {
      this.toastService.error(error?.error?.message ?? 'Não foi possível redefinir a senha.');
    } finally {
      this.isLoading = false;
    }
  }

  public goToLogin(): void {
    void this.router.navigateByUrl('/login');
  }

  private validatePassword(password: string, confirmPassword: string): string | null {
    if (!password?.trim() || !confirmPassword?.trim()) {
      return 'Preencha a nova senha e a confirmação.';
    }

    if (password.length < 6) {
      return 'É necessário enviar uma senha com 6 ou mais caracteres.';
    }

    if (password !== confirmPassword) {
      return 'As senhas não coincidem.';
    }

    return null;
  }

  private getResetPasswordCode(): string {
    const params = this.route.snapshot.queryParamMap;
    return (params.get('code') ?? params.get('token') ?? params.get('resetPasswordCode') ?? '').trim();
  }

}
